import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Button,
} from '@monksflow/monks-ui';
import type { ProjectListItem } from '@/entities/project';
import { OpportunityOverlayToolbar } from './components';
import type { OpportunityDetailOverlayProps } from './OpportunityDetailOverlay.types';
import './OpportunityDetailOverlay.styles.css';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line i18next/no-literal-string -- DOM attribute name, not user-facing copy.
const INERT_ATTR = 'inert';

export function OpportunityDetailOverlay<T>({
  items,
  renderItem,
  onClose,
  initialIndex = 0,
  onItemChange,
  getItemId,
  getIsFavorite,
  onToggleFavorite,
  isFavoriteLoading = false,
  workspaceId,
  onProjectSelected,
  renderBriefModals,
}: OpportunityDetailOverlayProps<T>) {
  const { t } = useTranslation('analysis');

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [direction, setDirection] = useState<1 | -1>(1);
  const count = items.length;

  // Get current item
  const currentItem = items[currentIndex];
  const hasBookmarkSupport = getItemId && getIsFavorite && onToggleFavorite;

  // Handle project selection from StartBriefPopover - delegate to parent
  const handleStartBriefProjectSelected = useCallback(
    (project: ProjectListItem) => {
      onProjectSelected?.(project);
    },
    [onProjectSelected],
  );

  // Latest-value refs so the keyboard listener (registered once, below) always
  // reads fresh values without re-registering on every navigation. See
  // Rule 3 — the reactions to a user action live in the handlers, not in
  // effects that watch state.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const onItemChangeRef = useRef(onItemChange);
  onItemChangeRef.current = onItemChange;

  // The target index is computed OUTSIDE the state updater, and `setDirection`
  // + the `onItemChange` notification run outside it too: updaters must be
  // pure, and StrictMode double-invokes them (which fired `onItemChange` twice
  // per navigation). `currentIndexRef` keeps these handlers stable.
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const goToIndex = useCallback((next: number, nextDirection: 1 | -1) => {
    currentIndexRef.current = next;
    setDirection(nextDirection);
    setCurrentIndex(next);
    onItemChangeRef.current?.(itemsRef.current[next], next);
  }, []);

  const goPrevious = useCallback(() => {
    const prev = currentIndexRef.current;
    if (prev <= 0) return;
    goToIndex(prev - 1, -1);
  }, [goToIndex]);

  const goNext = useCallback(() => {
    const prev = currentIndexRef.current;
    if (prev >= itemsRef.current.length - 1) return;
    goToIndex(prev + 1, 1);
  }, [goToIndex]);

  // Navigation actions
  const navigationActions = useMemo(
    () => ({
      canGoPrevious: currentIndex > 0,
      canGoNext: currentIndex < count - 1,
      goPrevious,
      goNext,
    }),
    [currentIndex, count, goPrevious, goNext],
  );

  // Arrows only; the dialog primitive owns Escape and the focus contract.
  const handleContentKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrevious();
      if (e.key === 'ArrowRight') goNext();
    },
    [goPrevious, goNext],
  );

  // Focus the toolbar, not the primitive's first tabbable: "Previous" is disabled
  // on item 0, so the default lands on an arbitrary button.
  const toolbarRef = useRef<HTMLDivElement>(null);
  const handleOpenAutoFocus = useCallback((e: Event) => {
    e.preventDefault();
    toolbarRef.current?.focus();
  }, []);

  // The primitive only marks the background `aria-hidden`, which leaves it in the
  // native Tab order; `inert` on the root removes it. The overlay portals to
  // <body>, outside the root, so it stays interactive.
  useLayoutEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    const wasInert = root.hasAttribute(INERT_ATTR);
    root.setAttribute(INERT_ATTR, '');
    return () => {
      if (!wasInert) root.removeAttribute(INERT_ATTR);
    };
  }, []);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        size="fullscreen"
        showCloseButton={false}
        onKeyDown={handleContentKeyDown}
        onOpenAutoFocus={handleOpenAutoFocus}
        // Strip the panel's opaque card styling so the blurred backdrop shows the
        // inner card, and restore the scroll-column layout.
        className="flex flex-col items-center justify-start gap-0 overflow-y-auto border-0 bg-transparent p-0 px-6 pt-6 pb-6 shadow-none outline-none"
        overlayClassName="bg-black/20 backdrop-blur-[12px]"
      >
        {/* Detail content has no stable heading, so name the dialog itself. */}
        <DialogTitle className="sr-only">
          {t('opportunity.opportunityDetailOverlay.opportunityDetail')}
        </DialogTitle>

        {/* Backdrop click target: the fullscreen panel covers the dialog overlay,
            so clicks outside the card land here. No tab stop — Escape closes. */}
        <div
          aria-hidden="true"
          data-testid="opportunity-overlay-backdrop"
          className="absolute inset-0"
          onClick={onClose}
        />

        {/* Labelled region focused on open; `group` not `toolbar` so arrow keys
            stay on item navigation rather than roving focus. */}
        <div
          ref={toolbarRef}
          tabIndex={-1}
          role="group"
          aria-label={t('opportunity.opportunityDetailOverlay.opportunityActions')}
          className="relative z-10 flex w-full max-w-300 shrink-0 items-center justify-between pb-3 outline-none"
        >
          <div className="flex w-full items-center justify-between">
            {/* Navigation buttons */}
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="secondary"
                      aria-label={t('opportunity.opportunityDetailOverlay.previous')}
                      disabled={!navigationActions.canGoPrevious}
                      onClick={navigationActions.goPrevious}
                      className="disabled:opacity-40 disabled:cursor-default"
                    >
                      <span className="nav-arrow-left">
                        <ArrowLeftIcon className="size-4 text-foreground" />
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t('opportunity.opportunityDetailOverlay.previousOpportunity')}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="secondary"
                      aria-label={t('opportunity.opportunityDetailOverlay.next')}
                      disabled={!navigationActions.canGoNext}
                      onClick={navigationActions.goNext}
                      className="disabled:opacity-40 disabled:cursor-default"
                    >
                      <span className="nav-arrow-right">
                        <ArrowRightIcon className="size-4 text-foreground" />
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t('opportunity.opportunityDetailOverlay.nextOpportunity')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Toolbar actions */}
            <OpportunityOverlayToolbar
              workspaceId={workspaceId}
              onStartBriefProjectSelected={handleStartBriefProjectSelected}
              bookmarkConfig={
                hasBookmarkSupport
                  ? {
                      itemId: getItemId(currentItem),
                      isFavorite: getIsFavorite(currentItem),
                      onToggle: onToggleFavorite,
                      isLoading: isFavoriteLoading,
                    }
                  : undefined
              }
              currentItem={currentItem}
              showSaveToProject={!!currentItem}
            />
          </div>
        </div>

        {/* Content box — inner AnimatePresence handles item swipes */}
        <div className="relative z-10 w-full max-w-300 overflow-hidden rounded-[10px] shadow">
          <AnimatePresence mode="popLayout" initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={{
                enter: (d: number) => ({ x: `${d * 100}%`, opacity: 0 }),
                center: { x: 0, opacity: 1 },
                exit: (d: number) => ({ x: `${d * -100}%`, opacity: 0 }),
              }}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              className="h-full w-full rounded-[10px] border border-border bg-background"
            >
              {renderItem(items[currentIndex], currentIndex)}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Brief generation modals - rendered by parent via slot pattern */}
        {renderBriefModals?.()}
      </DialogContent>
    </Dialog>
  );
}
